import { Link, useNavigate } from 'react-router-dom';
import { Button, buttonVariants } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Footer } from '../components/Footer';
import { HomeBannersCarousel } from '../components/layout/HomeBannersCarousel';
import { MagnifyingGlass, Car, House, DeviceMobile, TShirt, Wrench, Plant, Briefcase, DotsThree, Heart, Phone, Hammer, Baby, Basketball, Storefront, List, X, CaretDown, User as UserIcon, ChatCircle, Star as StarIcon, GearSix, SignOut, Globe, Monitor, FlowerLotus, GearFine, Bell, Faders, Eye, UsersThree, Tag, Plus } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { CurrencySelector } from '../components/CurrencySelector';
import { UserMenuPanel } from '../components/UserMenuPanel';
import { setCategoryCache } from '../utils/categoryNames';
import axios from 'axios';
import { CategoriesSection } from '../components/landing/CategoriesSection';
import { SectionSkeleton } from '../components/landing/SectionSkeleton';
import { BottomNav } from '../components/BottomNav';
import { useElementHeight } from '../hooks/use-element-height';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner';

// Below-the-fold sections are code-split to shrink the initial bundle
// and speed up the first paint on mobile.
const LatestListingsGrid = lazy(() =>
  import('../components/landing/LatestListingsGrid').then((m) => ({ default: m.LatestListingsGrid }))
);
const MarketInsights = lazy(() =>
  import('../components/MarketInsights').then((m) => ({ default: m.MarketInsights }))
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isStaff } = useAuth();
  const { branding } = useBranding();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [platformStats, setPlatformStats] = useState(null);
  const [latestListings, setLatestListings] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [autoSortConfig, setAutoSortConfig] = useState({ enabled: false, threshold: 10 });
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const [navBarRef, navBarHeight] = useElementHeight();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    axios.get(`${API}/platform/stats`).then(res => setPlatformStats(res.data)).catch(() => {});
    axios.get(`${API}/listings?sort=newest&limit=10`).then(res => setLatestListings(res.data.listings || [])).catch(() => {});
    // limit=100 so every active category gets a real listing count — only
    // used for sorting when the admin's "auto-sort" toggle is ON.
    axios.get(`${API}/top-categories?limit=100`).then(res => {
      const counts = {};
      (res.data || []).forEach((c) => { counts[c.value] = c.count; });
      setCategoryCounts(counts);
    }).catch(() => {});
    axios.get(`${API}/platform-config`).then(res => {
      setAutoSortConfig({
        enabled: !!res.data.category_auto_sort_enabled,
        threshold: res.data.category_auto_sort_threshold ?? 10,
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/notifications/unread-count`, { withCredentials: true })
      .then(res => setUnreadCount(res.data.unread_count)).catch(() => {});
  }, [user]);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/browse?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Icon map for known category keys (fallback to DotsThree for new categories)
  const ICON_MAP = {
    vehicles: Car, auto_parts: GearFine, real_estate: House,
    phones_accessories: Phone, electronics_appliances: Monitor,
    services: Wrench, agriculture_animals: Plant, fashion_beauty: TShirt,
    home_garden: FlowerLotus, construction_materials: Hammer,
    mother_baby: Baby, sports_hobbies: Basketball, jobs: Briefcase,
    business_equipment: Storefront, other: DotsThree
  };

  const [allCategories, setAllCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => {
      setAllCategories(res.data);
      setCategoryCache(res.data);
    }).catch(() => {});
  }, []);

  // Manual order (admin drag & drop, already the order /api/categories
  // returns) is used by default. When the admin's "auto-sort" toggle is ON,
  // categories at/above the threshold are promoted to the top sorted by
  // active listing count; the rest keep their manual relative order.
  const categoriesWithMeta = allCategories.map(c => ({
    ...c, icon: ICON_MAP[c.value] || DotsThree, count: categoryCounts[c.value] || 0,
  }));

  let categories = categoriesWithMeta;
  if (autoSortConfig.enabled) {
    const promoted = categoriesWithMeta.filter(c => c.count >= autoSortConfig.threshold)
      .sort((a, b) => b.count - a.count);
    const rest = categoriesWithMeta.filter(c => c.count < autoSortConfig.threshold);
    categories = [...promoted, ...rest];
  }

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      {/* Top Navigation Bar — fixed (matches shared Header treatment), so it
          stays visible while the page scrolls beneath it. */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-border/30" data-testid="landing-nav">
        <div ref={navBarRef}>
        <EmailVerificationBanner />
        <div className="max-w-6xl mx-auto flex items-center justify-between py-4 px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            {branding?.logo_path ? (
              <img
                src={branding.logo_path.startsWith('http') ? branding.logo_path : `${API}/files/${branding.logo_path}`}
                alt={branding?.platform_name || 'ATÔ'}
                className="h-9 w-auto object-contain"
              />
            ) : (
              <div className="flex flex-col md:flex-row md:items-baseline md:gap-2 min-w-0">
                <span className="text-2xl font-black font-heading text-text_strong leading-none">
                  {branding?.platform_name || 'ATÔ'}
                </span>
                <span
                  className="text-[10px] md:text-xs text-text_muted leading-tight mt-0.5 md:mt-0 md:whitespace-nowrap md:truncate md:max-w-[220px] lg:max-w-none"
                  title={(branding?.taglines?.[(i18n.language || 'fr').split('-')[0]] || '').trim() || t('home.tagline')}
                  data-testid="landing-tagline"
                >
                  {(branding?.taglines?.[(i18n.language || 'fr').split('-')[0]] || '').trim() || t('home.tagline')}
                </span>
              </div>
            )}
          </Link>
          
          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/create"
              data-testid="landing-nav-create"
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
            >
              <Plus size={14} weight="bold" />
              {t('nav.postAd')}
            </Link>
            <LanguageSwitcher />
            <CurrencySelector />
            {user ? (
              <>
                <button
                  onClick={() => navigate('/notifications')}
                  className="relative p-2 rounded-full hover:bg-surface_muted transition-colors"
                  data-testid="landing-notification-bell"
                >
                  <Bell size={22} weight={unreadCount > 0 ? 'fill' : 'regular'} className={unreadCount > 0 ? 'text-primary' : 'text-text_muted'} />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setAccountOpen(!accountOpen)}
                    className="flex items-center gap-1.5 text-sm font-medium text-text_main hover:text-primary transition-colors"
                    data-testid="my-account-btn"
                  >
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold overflow-hidden">
                      {user.picture ? (
                        <img src={user.picture.startsWith('http') ? user.picture : `${API}/files/${user.picture}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        user.name?.charAt(0).toUpperCase() || 'U'
                      )}
                    </div>
                    <CaretDown size={14} className={`transition-transform ${accountOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {accountOpen && (
                    <UserMenuPanel onClose={() => setAccountOpen(false)} variant="dropdown" />
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
                  {t('auth.login')}
                </Link>
                <Link to="/register" data-testid="nav-register" className={buttonVariants({ variant: 'accent', size: 'sm' })}>
                  {t('auth.register')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <LanguageSwitcher />
            <CurrencySelector />
            {user && (
              <button
                onClick={() => navigate('/notifications')}
                className="relative p-2 rounded-full hover:bg-surface_muted transition-colors"
                data-testid="landing-notification-bell-mobile"
              >
                <Bell size={22} weight={unreadCount > 0 ? 'fill' : 'regular'} className={unreadCount > 0 ? 'text-primary' : 'text-text_muted'} />
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            )}
            {user && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold overflow-hidden"
                data-testid="mobile-avatar-btn"
              >
                {user.picture ? (
                  <img src={user.picture.startsWith('http') ? user.picture : `${API}/files/${user.picture}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  user.name?.charAt(0).toUpperCase() || 'U'
                )}
              </button>
            )}
            {!user && (
              <Link to="/login">
                <Button className="bg-primary hover:bg-primary-hover text-white text-sm px-4 py-2" data-testid="mobile-login-btn">
                  {t('auth.login')}
                </Button>
              </Link>
            )}
          </div>
        </div>
        </div>

        {/* Mobile Menu (logged-in users only) — overlays below the fixed
            bar; kept out of the ref'd row above so opening/closing it
            doesn't shift the spacer height / page content underneath. */}
        {mobileMenuOpen && user && (
          <div className="md:hidden mx-auto max-w-6xl px-6 pb-3 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-menu">
            <UserMenuPanel onClose={() => setMobileMenuOpen(false)} variant="mobile" />
          </div>
        )}
      </nav>
      {/* Spacer reserving the fixed nav bar's height so content never loads
          hidden underneath it (height tracks the bar live). */}
      <div style={{ height: navBarHeight }} aria-hidden="true" data-testid="landing-nav-spacer" />

      {/* Hero Section — iter 84 uses the Layout > Banners carousel. */}
      <div className="bg-cream">
        <HomeBannersCarousel />

        <div className="max-w-5xl mx-auto px-6 pt-4">
          <div className="mb-3" />

          {/* Search Bar — matches Browse page mobile design */}
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center bg-white rounded-md shadow-md border border-border overflow-hidden">
              <Input
                type="text"
                placeholder={t('home.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 border-0 focus:ring-0 focus:outline-none bg-transparent text-base h-12 px-4"
                data-testid="search-input"
              />
              <Button
                onClick={() => navigate(`/browse?search=${encodeURIComponent(searchQuery || '')}&open_filters=1`)}
                variant="outline"
                className="border-0 border-l border-border rounded-none h-12 px-4 text-text_muted hover:text-text_main hover:bg-surface_muted"
                data-testid="filter-button"
              >
                <Faders size={22} />
              </Button>
              <Button
                onClick={handleSearch}
                className="bg-primary hover:bg-primary-hover text-white rounded-none h-12 px-5"
                data-testid="search-button"
              >
                <MagnifyingGlass size={22} weight="bold" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Section — unified, most-active-first, hybrid layout */}
      <CategoriesSection categories={categories} />

      {/* Latest Listings Section (lazy-loaded below the fold) */}
      <Suspense fallback={<SectionSkeleton height="h-[600px]" testId="latest-listings-skeleton" />}>
        <LatestListingsGrid listings={latestListings} platformStats={platformStats} />
      </Suspense>

      {/* Platform Activity (Market Insights) — lazy-loaded further below */}
      <Suspense fallback={<SectionSkeleton height="h-80" testId="market-insights-skeleton" />}>
        <MarketInsights />
      </Suspense>

      <Footer />

      <BottomNav />
    </div>
  );
};
